import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Fireflies drifting over the campfire header. Pure decoration: each one is a small glow
 * that wanders along its own path and pulses on its own rhythm. Positions are fixed (not
 * random) so the server and the client render the same thing; they sit on the swamp and
 * around the fire, not on the title.
 */

interface Fly {
  x: number; // % of header width
  y: number; // % of header height
  size: number; // px
  dur: number; // s, drift loop
  blink: number; // s, glow loop
  delay: number; // s
  path: 0 | 1 | 2;
}

const FLIES: Fly[] = [
  { x: 4, y: 72, size: 7, dur: 9, blink: 2.6, delay: 0, path: 0 },
  { x: 9, y: 86, size: 9, dur: 11, blink: 3.1, delay: -2, path: 1 },
  { x: 16, y: 64, size: 6, dur: 8, blink: 2.2, delay: -4, path: 2 },
  { x: 22, y: 90, size: 8, dur: 12, blink: 3.6, delay: -1, path: 0 },
  { x: 30, y: 78, size: 6, dur: 10, blink: 2.8, delay: -6, path: 1 },
  { x: 38, y: 92, size: 7, dur: 9, blink: 2.4, delay: -3, path: 2 },
  { x: 44, y: 44, size: 6, dur: 13, blink: 3.3, delay: -5, path: 0 },
  { x: 52, y: 30, size: 7, dur: 11, blink: 2.9, delay: -7, path: 1 },
  { x: 58, y: 56, size: 5, dur: 9, blink: 2.1, delay: -2, path: 2 },
  { x: 64, y: 22, size: 6, dur: 12, blink: 3.4, delay: -8, path: 0 },
  { x: 70, y: 40, size: 8, dur: 10, blink: 2.7, delay: -1, path: 1 },
  { x: 76, y: 18, size: 6, dur: 14, blink: 3.8, delay: -9, path: 2 },
  { x: 82, y: 52, size: 7, dur: 9, blink: 2.5, delay: -4, path: 0 },
  { x: 88, y: 30, size: 6, dur: 11, blink: 3.0, delay: -6, path: 1 },
  { x: 93, y: 70, size: 8, dur: 10, blink: 2.3, delay: -3, path: 2 },
  { x: 97, y: 44, size: 5, dur: 12, blink: 3.2, delay: -5, path: 0 },
  { x: 48, y: 84, size: 7, dur: 10, blink: 2.6, delay: -7, path: 1 },
  { x: 66, y: 88, size: 6, dur: 11, blink: 3.5, delay: -2, path: 2 },
];

const drift = [
  keyframes`
    0% { transform: translate(0, 0); }
    25% { transform: translate(18px, -14px); }
    50% { transform: translate(34px, 4px); }
    75% { transform: translate(14px, 16px); }
    100% { transform: translate(0, 0); }
  `,
  keyframes`
    0% { transform: translate(0, 0); }
    30% { transform: translate(-22px, -18px); }
    60% { transform: translate(-6px, -34px); }
    100% { transform: translate(0, 0); }
  `,
  keyframes`
    0% { transform: translate(0, 0); }
    35% { transform: translate(12px, 20px); }
    70% { transform: translate(-16px, 8px); }
    100% { transform: translate(0, 0); }
  `,
];

const glow = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.7); }
  45% { opacity: 1; transform: scale(1); }
  60% { opacity: 0.9; transform: scale(0.95); }
`;

export const Fireflies: React.FC = () => (
  <Layer data-fireflies aria-hidden="true">
    {FLIES.map((f, i) => (
      <Drift
        key={i}
        data-firefly
        style={{
          left: `${f.x}%`,
          top: `${f.y}%`,
          animationDuration: `${f.dur}s`,
          animationDelay: `${f.delay}s`,
        }}
        $path={f.path}
      >
        <span style={{ width: f.size, height: f.size, animationDuration: `${f.blink}s`, animationDelay: `${f.delay / 2}s` }} />
      </Drift>
    ))}
  </Layer>
);

const Layer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const Drift = styled.i<{ $path: 0 | 1 | 2 }>`
  position: absolute;
  display: block;
  animation: ${(p) => drift[p.$path]} 10s ease-in-out infinite;
  will-change: transform;
  span {
    display: block;
    border-radius: 50%;
    background: #fff6a8;
    box-shadow: 0 0 6px 2px rgba(255, 236, 110, 0.9), 0 0 18px 6px rgba(255, 214, 60, 0.45);
    animation: ${glow} 3s ease-in-out infinite;
  }
`;
